import get from 'lodash/get'
import * as Redux from 'redux'
import * as Form from '../../components/form'
import * as IsActive from '../../components/is_active'
import * as Sortable from '../../components/sortable'
import * as Request from '../../request'
import * as GlobalUtils from '../../utils'
import * as Actions from './actions'
import * as AttributesConstants from './attributes'
import * as Constants from './constants'
import * as Types from './types'
import * as Utils from './utils'

const payloadReducer = (state: Types.Attribute[] = [], action: any) => {
    switch (action.type) {
      case Request.requestConstant(Constants.FETCH_MEMBER_DETAILS):
        return []
      case Request.errorConstant(Constants.FETCH_MEMBER_DETAILS):
        return []
      case Request.successConstant(Constants.FETCH_MEMBER_DETAILS):
          return get(action.payload.response.basic.results[0], 'data.attributes', null) ?
          action.payload.response.basic.results[0].data.attributes.map((attribute: Types.Attribute, i: number) => {
          return {
            ...attribute,
            index: i,
          }
        }) || [] : state
      case AttributesConstants.ADD_MEMBER_DETAIL_ATTRIBUTE:
          return [
                ...state,
                {
                  index: state.length,
                  is_editing: true,
                  name: '',
                  value: '',
                },
              ]
        case AttributesConstants.CLICK_MEMBER_DETAIL_ATTRIBUTE:
          return [
                ...state.map(
                  (attribute) => ({
                    ...attribute,
                    is_editing: attribute.index === action.index,
                  }),
                ),
              ]
        case AttributesConstants.EDIT_MEMBER_DETAIL_ATTRIBUTE:
          const updatedAttribute = {
            ...state[action.index],
            is_dirty: true,
            name: action.field.name,
            value: action.field.value,
          }
          return GlobalUtils.replace(state, action.index, updatedAttribute)
        case Request.successConstant(Constants.UPDATE_MEMBER_DETAILS):
            return [
              ...state.map(
                (attribute) => ({
                  ...attribute,
                  is_editing: false,
                }),
              ),
            ]
      default:
        return state
    }
  }

const stateReducer = (event: Sortable.ReducerEvent<Sortable.SortableObject>) =>
    (state: Types.Actions, action: Sortable.AllActions): Types.Actions => {
  switch (action.type) {
    case Sortable.sortTableConstant(event.requestEvent):
    case Sortable.sortTableConstant(event.sortEvent):
      return {
        ...state,
        last_week: Sortable.sortState(state.last_week, action.options.value),
      }
    case Request.errorConstant(event.requestEvent):
    case Request.successConstant(event.requestEvent):
      return {
        ...state,
        last_week: Sortable.sortState(
          (event.normalizer ?
           event.normalizer(action.payload.response.actions.results.last_week) :
           action.payload.response.actions.results.last_week),
          [event.defaultSort || Sortable.identitySort]),
      }
    default:
      return state
  }
}

type RequestAction = Request.RequestAction & Request.SuccessAction

const generalMemberDetailsReducer = (state: Types.MemberGeneral, action: RequestAction) => {
  switch (action.type) {
    case Request.successConstant(Constants.FETCH_MEMBER_DETAILS):
      return {
        ...state,
        data: {
          ...state.data,
          ...Utils.basicFormat(action.payload.response.basic.results[0].data),
        },
        links: {
          ...state.links,
          ...action.payload.response.basic.results[0].links,
        },
      }
    case Request.requestConstant(Constants.FETCH_MEMBER_DETAILS):
      return {}
    case Request.successConstant(Constants.UPDATE_MEMBER_DETAILS):
      return {
          ...state,
          data: {
            ...state.data,
            ...action.requestAction.options.payload,
          },
          isSaving: false,
        }
    case Request.requestConstant(Constants.UPDATE_MEMBER_DETAILS_UPDATE_STATUS):
        return {
          ...state,
          data: {
            ...state.data,
            ...action.options.payload,
          },
        }
    case Request.errorConstant(Constants.UPDATE_MEMBER_DETAILS_UPDATE_STATUS):
        return {
          ...state,
          data: {
            ...state.data,
            ...Utils.revertStatus(action.requestAction.options.payload),
          },
        }
    default:
      return state || {}
  }
}

const emptyMetaDataState = {
  errors: '',
  next_link: '',
  record_count: 0,
  request: '',
  timestamp: '',
  token_expiration: '',
  total_query_count: 0,
}

export const metaDataReducer = (state: Types.MetaData = emptyMetaDataState, action: Request.SuccessAction) => {
  switch (action.type) {
    case Constants.FETCH_MEMBER_SUCCESS:
      return {
        ...state,
        record_count: 1,
        total_query_count: 1,
      }
    case Constants.FETCH_MEMBER_SUCCESS:
      return action.payload.response.basic.metadata
    default:
      return state
  }
}

const initialViewState: Sortable.SortableViewState = {
  isLoading: false,
  sort: [],
}

export const memberDetailsViewReducer =
(state: Types.ViewState = initialViewState, action: Actions.FetchMemberDetailsAction) => {
  switch (action.type) {
    case Request.requestConstant(Constants.FETCH_MEMBER_DETAILS):
      return {
        ...state,
        isLoading: true,
      }
    case Request.successConstant(Constants.FETCH_MEMBER_DETAILS):
      return {
        ...state,
        isLoading: false,
      }
    default:
      return state
  }
}

const memberActionsReducer = (state: Types.Action[], action: RequestAction) => {
  switch (action.type) {
    case Request.successConstant(Constants.FETCH_MEMBER_DETAILS):
      return(Utils.actionsFormat(action.payload.response.actions.results))
    case Request.requestConstant(Constants.FETCH_MEMBER_DETAILS):
      return []
    default:
      return state || []
  }
}

const memberTopicsReducer = (state: Types.Topic[], action: RequestAction) => {
  switch (action.type) {
    case Request.successConstant(Constants.FETCH_MEMBER_DETAILS):
      return action.payload.response.topics.results.map((result: any) => ({
          ...result,
          count: result.weight,
          value: result.topic,
        }))
    case Request.requestConstant(Constants.FETCH_MEMBER_DETAILS):
      return []
    default:
      return state || []
  }
}

const reducerEvent: Sortable.ReducerEvent<Types.Action> = {
  defaultSort: {field: 'id', dir: 'desc'},
  requestEvent: Constants.FETCH_MEMBER_DETAILS,
}

export const reducer = Redux.combineReducers({
  actions: Request.reducerChain([
    memberActionsReducer,
    stateReducer(reducerEvent),
  ]),
  attributes: payloadReducer,
  detailsForm: Request.reducerChain([
    Form.createReducer(Constants.MEMBER_DETAILS_PREFIX),
    AttributesConstants.memberDetailsReducer,
  ]),
  general: generalMemberDetailsReducer,
  metaData: metaDataReducer,
  topics: memberTopicsReducer,
  view: Request.reducerChain([
    memberDetailsViewReducer,
    IsActive.filterReducer(Sortable.viewReducer(reducerEvent), Constants.FETCH_MEMBER_DETAILS, initialViewState),
  ]),
})



// WEBPACK FOOTER //
// ./src/pages/member-details/reducer.ts